import * as THREE from 'three';
import { useAppStore } from '../../state/store';
import { useDocumentStore } from '../../state/useDocumentStore';
import { entityMeshRegistry } from '../../rendering/toolEventBus';
import { resolveVisibility, currentVisibilityInput } from '../../rendering/visibility';
import { sceneSourceOf } from '../../core/scene';
import type { SketchHit } from './sketchToolBus';

/**
 * Cross-workbench selection helpers for the Sketch workbench's shared
 * modify tools (B4 edit bridge, sketch side).
 *
 * Sketch citizens picked through the sketch bus live in the sketch store's
 * selection space; foreign citizens (CAD entities, BIM elements) picked
 * through the reference layers live in the shared app selection. The
 * modify tools act on BOTH: the sketch halves stay native, the foreign
 * halves route through the edit router — these helpers resolve which ids
 * are foreign, and where the pointer actually lands on foreign geometry.
 */

/**
 * App-selection ids that resolve to LIVE scene citizens, excluding sketch
 * ids already owned by the native sketch gesture (`excludeSketchIds`) so a
 * mixed selection never moves an object twice. Locked/hidden objects stay
 * out (the same gate the shared click dispatch applies).
 */
export function foreignSelectionIds(excludeSketchIds?: ReadonlySet<string>): string[] {
    const app = useAppStore.getState();
    const ledger = useDocumentStore.getState().sceneObjects;
    const visInput = currentVisibilityInput();
    const out: string[] = [];
    for (const id of app.selectedIds) {
        const source = sceneSourceOf(id);
        if (!source) continue;
        if (source === 'sketch' && excludeSketchIds?.has(id)) continue;
        const sceneObject = ledger[id];
        if (sceneObject) {
            const r = resolveVisibility(sceneObject, visInput);
            if (!r.visible || r.locked) continue;
        }
        out.push(id);
    }
    return out;
}

/**
 * Foreign scene citizen under a sketch bus hit's pointer position (the
 * Eraser path): raycast the shared entity registry from the camera through
 * the native pointer px and return the first hit whose root resolves to a
 * non-sketch citizen. Sketch geometry is skipped — it owns its own bus
 * picks, and a 'plane' hit means the pointer missed every sketch face.
 */
export function foreignIdUnderHit(
    hit: SketchHit,
    camera: THREE.Camera,
    domElement: HTMLCanvasElement
): string | null {
    if (!hit.pointerPx) return null;
    const rect = domElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const pointerNdc = new THREE.Vector2(
        ((hit.pointerPx.x - rect.left) / rect.width) * 2 - 1,
        -((hit.pointerPx.y - rect.top) / rect.height) * 2 + 1
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(pointerNdc, camera);
    const hits = raycaster.intersectObjects([...entityMeshRegistry.values()], true);
    const ledger = useDocumentStore.getState().sceneObjects;
    const visInput = currentVisibilityInput();
    for (const candidate of hits) {
        let obj: THREE.Object3D | null = candidate.object;
        while (obj) {
            const id = obj.userData?.entityId as string | undefined;
            if (id) {
                const source = sceneSourceOf(id);
                if (source && source !== 'sketch') {
                    const sceneObject = ledger[id];
                    if (sceneObject) {
                        const r = resolveVisibility(sceneObject, visInput);
                        if (!r.visible || r.locked) break;
                    }
                    return id;
                }
                break;
            }
            obj = obj.parent;
        }
    }
    return null;
}
